import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2Icon, RocketIcon, TargetIcon, UsersIcon, BriefcaseIcon, UploadIcon, ZapIcon, StarIcon, SparklesIcon, CrownIcon, CreditCardIcon, ShieldIcon, AlertCircleIcon, LinkedInIcon } from './BrandIcons';
import { X, Mail, ArrowRight, ArrowLeft, Check, ChevronRight } from 'lucide-react';
import { C, F } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { BrandedLoader, PaymentLoader } from './BrandedLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Auth modes
const MODES = {
  CHOOSE: 'choose',         // Choose Sign In or Sign Up
  SIGNIN: 'signin',         // Sign In options
  SIGNUP: 'signup',         // Sign Up options
  EMAIL_INPUT: 'email_input',
  OTP_VERIFY: 'otp_verify',
  NAME_INPUT: 'name_input', // For new email signups
  // Onboarding steps (for new users)
  ONBOARD_PERSONA: 'onboard_persona',
  ONBOARD_GOAL: 'onboard_goal',
  ONBOARD_PLAN: 'onboard_plan',  // Plan selection step
  PAYMENT: 'payment',       // Payment step for paid plans
  CREATING: 'creating',
  SUCCESS: 'success'
};

// Persona options for onboarding - icons are created as functions to work with useId
const personasConfig = [
  { id: 'founder', Icon: RocketIcon, title: 'Founder', subtitle: 'Raising capital', color: '#00D4AA' },
  { id: 'sales', Icon: TargetIcon, title: 'Sales', subtitle: 'Closing deals', color: '#0082FB' },
  { id: 'pm', Icon: BriefcaseIcon, title: 'Product', subtitle: 'Getting approval', color: '#8B5CF6' },
  { id: 'accelerator', Icon: UsersIcon, title: 'Accelerator', subtitle: 'Portfolio scoring', color: '#FFB800' }
];

// Goals based on persona
const goalsByPersona = {
  founder: [
    { id: 'score', label: 'Get my deck scored' },
    { id: 'improve', label: 'Improve my deck' },
    { id: 'strategy', label: 'Get expert help' },
    { id: 'build', label: 'Build my deck' }
  ],
  sales: [
    { id: 'score', label: 'Score my sales deck' },
    { id: 'improve', label: 'Optimize for close' },
    { id: 'battlecards', label: 'Get battle cards' },
    { id: 'sequences', label: 'Outreach sequences' }
  ],
  pm: [
    { id: 'score', label: 'Score my deck' },
    { id: 'improve', label: 'Strengthen narrative' },
    { id: 'framework', label: 'ROI framework' },
    { id: 'strategy', label: 'Expert review' }
  ],
  accelerator: [
    { id: 'bulk', label: 'Score cohort decks' },
    { id: 'benchmark', label: 'Portfolio benchmarks' },
    { id: 'api', label: 'API access' },
    { id: 'enterprise', label: 'Custom solution' }
  ]
};

// Plans for selection during onboarding
// Plans for onboarding flow - icons as components
const onboardingPlansConfig = [
  {
    id: 'free',
    name: 'Free',
    price_monthly_inr: 0,
    price_monthly_usd: 0,
    price_yearly_inr: 0,
    price_yearly_usd: 0,
    Icon: ZapIcon,
    features: ['1 Deck Analysis', '5 Dimension Score', 'Top 5 Issues'],
    color: '#6B7280',
    highlight: false,
    credits: 1
  },
  {
    id: 'starter',
    name: 'Starter',
    price_monthly_inr: 999,
    price_monthly_usd: 12,
    price_yearly_inr: 9990,  // ~17% savings
    price_yearly_usd: 120,
    Icon: StarIcon,
    features: ['5 Decks/month', 'Investor Insights', 'Benchmarks'],
    color: '#3B82F6',
    highlight: false,
    credits: 5
  },
  {
    id: 'growth',
    name: 'Growth',
    price_monthly_inr: 2499,
    price_monthly_usd: 30,
    price_yearly_inr: 24990,  // ~17% savings
    price_yearly_usd: 300,
    Icon: SparklesIcon,
    features: ['15 Decks/month', 'Pitch Canvas', 'Priority Support'],
    color: '#00D4FF',
    highlight: true,
    badge: 'POPULAR',
    credits: 15
  },
  {
    id: 'scale',
    name: 'Scale',
    price_monthly_inr: 4999,
    price_monthly_usd: 60,
    price_yearly_inr: 49990,  // ~17% savings
    price_yearly_usd: 600,
    Icon: CrownIcon,
    features: ['50 Decks/month', 'Battle Cards', 'Strategy Calls'],
    color: '#FFB800',
    highlight: false,
    credits: 50
  }
];

export const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { theme } = useTheme();
  const [mode, setMode] = useState(MODES.CHOOSE);
  const [isSignUp, setIsSignUp] = useState(false);
  
  // Auth data
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [mockOtp, setMockOtp] = useState('');
  
  // Onboarding data
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currency, setCurrency] = useState('INR'); // Currency selection
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or yearly
  
  // Payment state
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const isDark = theme === 'dark';
  
  // Helper function to get price based on currency and billing cycle
  const getPrice = (plan) => {
    if (!plan) return 0;
    if (billingCycle === 'yearly') {
      return currency === 'USD' ? (plan.price_yearly_usd || 0) : (plan.price_yearly_inr || 0);
    }
    return currency === 'USD' ? (plan.price_monthly_usd || 0) : (plan.price_monthly_inr || 0);
  };
  
  // Helper function to get monthly equivalent for yearly display
  const getMonthlyEquivalent = (plan) => {
    if (!plan || billingCycle !== 'yearly') return null;
    const yearlyPrice = getPrice(plan);
    return Math.round(yearlyPrice / 12);
  };
  
  // Helper function to format price
  const formatPrice = (amount) => {
    if (currency === 'USD') {
      return `$${amount.toLocaleString('en-US')}`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Load Razorpay script on mount
  useEffect(() => {
    loadRazorpayScript();
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(MODES.CHOOSE);
      setIsSignUp(false);
      setEmail('');
      setOtp('');
      setName('');
      setMockOtp('');
      setSelectedPersona(null);
      setSelectedGoal(null);
      setSelectedPlan(null);
      setCurrency('INR');
      setBillingCycle('monthly');
      setError('');
      setPaymentError('');
      setUser(null);
      setToken(null);
    }
  }, [isOpen]);

  // Handle LinkedIn auth
  const handleLinkedIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/linkedin/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_signup: isSignUp })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUser(data.user);
        setToken(data.token);
        
        // Use ONLY backend's response to determine if new user
        // Backend returns is_new_user: true for first-time users, false for existing
        if (data.is_new_user) {
          // New user - go through onboarding
          setMode(MODES.ONBOARD_PERSONA);
        } else {
          // Existing user - skip onboarding, direct to success
          finishAuth(data.user, data.token);
        }
      } else {
        setError(data.message || 'Authentication failed');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
      console.error('LinkedIn auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Send OTP
  const handleSendOTP = async (e) => {
    e?.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/email/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMockOtp(data.mock_otp);
        
        // Check if user exists for sign-in flow
        if (!isSignUp && !data.user_exists) {
          setError('No account found. Please sign up first.');
          return;
        }
        
        if (isSignUp && data.user_exists) {
          setError('Account already exists. Please sign in.');
          return;
        }
        
        setMode(MODES.OTP_VERIFY);
      } else {
        setError(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e?.preventDefault();
    if (!otp || otp.length !== 6) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/email/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          otp, 
          name: isSignUp ? name : undefined 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Invalid OTP');
        return;
      }
      
      if (data.success) {
        setUser(data.user);
        setToken(data.token);
        
        // Check if this is a new user based on backend response
        const isNewUser = data.is_new_user || isSignUp;
        
        if (isNewUser) {
          // New user - go through onboarding
          setMode(MODES.ONBOARD_PERSONA);
        } else {
          // Existing user - skip onboarding, direct to success
          finishAuth(data.user, data.token);
        }
      } else if (data.message === 'Name required for new account') {
        setMode(MODES.NAME_INPUT);
      } else {
        setError(data.message || 'Verification failed');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle name submission for email signup
  const handleNameSubmit = (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    handleVerifyOTP();
  };

  // Handle persona selection
  const handlePersonaSelect = (persona) => {
    setSelectedPersona(persona);
    setMode(MODES.ONBOARD_GOAL);
  };

  // Handle goal selection
  const handleGoalSelect = (goal) => {
    setSelectedGoal(goal);
    setMode(MODES.ONBOARD_PLAN);
  };

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  // Handle plan submission - route to payment for paid plans
  const handlePlanSubmit = () => {
    if (!selectedPlan) return;
    
    // Free plan - skip payment
    if (selectedPlan.id === 'free' || selectedPlan.price_monthly_inr === 0) {
      setMode(MODES.CREATING);
      setTimeout(() => {
        const finalUser = {
          ...user,
          persona: selectedPersona,
          goal: selectedGoal,
          plan: selectedPlan,
          onboarded: true
        };
        finishAuth(finalUser, token);
      }, 2500);
    } else {
      // Paid plan - go to payment
      setMode(MODES.PAYMENT);
    }
  };

  // Handle payment for paid plans
  const handlePayment = async () => {
    if (!selectedPlan || getPrice(selectedPlan) === 0) return;
    
    setPaymentLoading(true);
    setPaymentError('');
    
    try {
      // Create order
      const orderPayload = {
        user_id: user?.id || user?.email || 'guest',
        user_email: user?.email || email || 'guest@example.com',
        user_name: user?.name || name || 'Guest User',
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle,
        currency: currency
      };
      
      const orderResponse = await fetch(`${API_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload)
      });
      
      if (!orderResponse.ok) {
        const errData = await orderResponse.json();
        throw new Error(errData.detail || 'Failed to create order');
      }
      
      const orderData = await orderResponse.json();
      
      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: 'DecX AI',
        description: orderData.description,
        order_id: orderData.order_id,
        handler: async function (response) {
          // Verify payment
          try {
            const verifyResponse = await fetch(`${API_URL}/api/payments/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                user_id: user?.id || user?.email || 'guest'
              })
            });
            
            const verifyData = await verifyResponse.json();
            
            if (verifyData.success) {
              // Payment successful - complete onboarding
              setMode(MODES.CREATING);
              setTimeout(() => {
                const finalUser = {
                  ...user,
                  persona: selectedPersona,
                  goal: selectedGoal,
                  plan: selectedPlan,
                  onboarded: true,
                  credits: verifyData.credits_added || selectedPlan.credits
                };
                finishAuth(finalUser, token);
              }, 2000);
            } else {
              setPaymentError('Payment verification failed. Please contact support.');
            }
          } catch (err) {
            setPaymentError('Payment verification failed. Please try again.');
          }
          setPaymentLoading(false);
        },
        prefill: {
          name: user?.name || name || '',
          email: user?.email || email || '',
          contact: ''
        },
        theme: {
          color: '#00D4FF'
        },
        modal: {
          ondismiss: function() {
            setPaymentLoading(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setPaymentError(response.error.description || 'Payment failed. Please try again.');
        setPaymentLoading(false);
      });
      razorpay.open();
      
    } catch (err) {
      setPaymentError(err.message || 'Something went wrong. Please try again.');
      setPaymentLoading(false);
    }
  };

  // Finish authentication and close modal
  const finishAuth = (userData, authToken) => {
    localStorage.setItem('decx_token', authToken);
    localStorage.setItem('decx_user', JSON.stringify(userData));
    setMode(MODES.SUCCESS);
    
    setTimeout(() => {
      onSuccess?.(userData, authToken);
    }, 1500);
  };

  // Input styles
  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    borderRadius: '12px',
    border: `2px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    color: isDark ? C.white : C.black,
    fontSize: '16px',
    fontFamily: "'Manrope', sans-serif",
    outline: 'none',
  };

  const buttonPrimary = {
    width: '100%',
    padding: '16px 24px',
    borderRadius: '12px',
    border: 'none',
    background: C.secondary,
    color: 'white',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: "'Manrope', sans-serif",
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
        }}
        onClick={onClose}
        data-testid="auth-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: isDark 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '24px',
            padding: '40px',
            maxWidth: mode.startsWith('onboard') || mode === MODES.CREATING ? '600px' : '440px',
            width: '100%',
            position: 'relative',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
          data-testid="auth-modal"
        >
          {/* Close Button */}
          {mode !== MODES.CREATING && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              }}
              data-testid="auth-modal-close"
            >
              <X size={24} />
            </button>
          )}

          {/* ============ CHOOSE SIGN IN / SIGN UP ============ */}
          {mode === MODES.CHOOSE && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h2 style={{ 
                  fontSize: '28px', 
                  fontWeight: 700, 
                  marginBottom: '8px',
                  fontFamily: F,
                  color: isDark ? C.white : C.black
                }}>
                  Welcome to <span style={{ color: C.secondary }}>Dec</span>X AI
                </h2>
                <p style={{ fontSize: '15px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  Transform your deck to DecX
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { setIsSignUp(false); setMode(MODES.SIGNIN); }}
                  style={{
                    ...buttonPrimary,
                    background: C.grad,
                  }}
                  data-testid="signin-btn"
                >
                  Sign In
                  <ArrowRight size={18} />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { setIsSignUp(true); setMode(MODES.SIGNUP); }}
                  style={{
                    ...buttonPrimary,
                    background: 'transparent',
                    border: `2px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                    color: isDark ? C.white : C.black,
                  }}
                  data-testid="signup-btn"
                >
                  Create Account
                  <ArrowRight size={18} />
                </motion.button>
              </div>

              {/* Mock Notice */}
              <div style={{
                marginTop: '24px',
                padding: '12px 16px',
                background: 'rgba(255,184,0,0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(255,184,0,0.3)',
              }}>
                <p style={{ fontSize: '12px', color: '#FFB800', textAlign: 'center', fontFamily: "'Manrope', sans-serif" }}>
                  Demo Mode: Auth is mocked for testing
                </p>
              </div>
            </motion.div>
          )}

          {/* ============ SIGN IN OPTIONS ============ */}
          {mode === MODES.SIGNIN && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(MODES.CHOOSE)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '24px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back
              </button>

              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h2 style={{ 
                  fontSize: '26px', 
                  fontWeight: 700, 
                  marginBottom: '8px',
                  fontFamily: F,
                  color: isDark ? C.white : C.black
                }}>
                  Welcome Back
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  Sign in to your account
                </p>
              </div>

              {/* LinkedIn Sign In */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleLinkedIn}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#0077B5',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                  fontFamily: "'Manrope', sans-serif",
                  opacity: loading ? 0.7 : 1,
                }}
                data-testid="linkedin-signin-btn"
              >
                {loading ? <Loader2Icon size={20} /> : <LinkedInIcon size={20} />}
                Sign in with LinkedIn
              </motion.button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '24px 0' }}>
                <div style={{ flex: 1, height: '1px', background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }} />
                <span style={{ fontSize: '13px', opacity: 0.5 }}>or</span>
                <div style={{ flex: 1, height: '1px', background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }} />
              </div>

              {/* Email Sign In */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                 onClick={() => window.location.href = `${process.env.REACT_APP_DECX_BASE_URL}/login-screen`}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  border: `2px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  background: 'transparent',
                  color: isDark ? C.white : C.black,
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="email-signin-btn"
              >
                <Mail size={20} />
                Sign in with Email
              </motion.button>

              <p style={{ 
                textAlign: 'center', 
                marginTop: '24px', 
                fontSize: '14px', 
                opacity: 0.7,
                fontFamily: "'Manrope', sans-serif" 
              }}>
                Don't have an account?{' '}
                <button 
                  onClick={() => { setIsSignUp(true); setMode(MODES.SIGNUP); }}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: C.secondary, 
                    cursor: 'pointer',
                    fontWeight: 600 
                  }}
                >
                  Sign up
                </button>
              </p>
            </motion.div>
          )}

          {/* ============ SIGN UP OPTIONS ============ */}
          {mode === MODES.SIGNUP && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(MODES.CHOOSE)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '24px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back
              </button>

              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h2 style={{ 
                  fontSize: '26px', 
                  fontWeight: 700, 
                  marginBottom: '8px',
                  fontFamily: F,
                  color: isDark ? C.white : C.black
                }}>
                  Create Your Account
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  Get started in under 2 minutes
                </p>
              </div>

              {/* LinkedIn Sign Up */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleLinkedIn}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#0077B5',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                  fontFamily: "'Manrope', sans-serif",
                  opacity: loading ? 0.7 : 1,
                }}
                data-testid="linkedin-signup-btn"
              >
                {loading ? <Loader2Icon size={20} /> : <LinkedInIcon size={20} />}
                Sign up with LinkedIn
              </motion.button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '24px 0' }}>
                <div style={{ flex: 1, height: '1px', background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }} />
                <span style={{ fontSize: '13px', opacity: 0.5 }}>or</span>
                <div style={{ flex: 1, height: '1px', background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }} />
              </div>

              {/* Email Sign Up */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.location.href = `${process.env.REACT_APP_DECX_BASE_URL}/registration-screen`}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  border: `2px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  background: 'transparent',
                  color: isDark ? C.white : C.black,
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="email-signup-btn"
              >
                <Mail size={20} />
                Sign up with Email
              </motion.button>

              <p style={{ 
                textAlign: 'center', 
                marginTop: '24px', 
                fontSize: '14px', 
                opacity: 0.7,
                fontFamily: "'Manrope', sans-serif" 
              }}>
                Already have an account?{' '}
                <button 
                  onClick={() => { setIsSignUp(false); setMode(MODES.SIGNIN); }}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: C.secondary, 
                    cursor: 'pointer',
                    fontWeight: 600 
                  }}
                >
                  Sign in
                </button>
              </p>
            </motion.div>
          )}

          {/* ============ NAME INPUT (for email signup) ============ */}
          {mode === MODES.NAME_INPUT && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(isSignUp ? MODES.SIGNUP : MODES.OTP_VERIFY)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '24px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back
              </button>

              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px', fontFamily: F, color: isDark ? C.white : C.black }}>
                  What's your name?
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  Let us know how to address you
                </p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) setMode(MODES.EMAIL_INPUT); }}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  style={inputStyle}
                  autoFocus
                  data-testid="name-input"
                />

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={!name.trim()}
                  style={{
                    ...buttonPrimary,
                    marginTop: '20px',
                    opacity: !name.trim() ? 0.5 : 1,
                    cursor: !name.trim() ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="continue-name-btn"
                >
                  Continue
                  <ArrowRight size={18} />
                </motion.button>
              </form>
            </motion.div>
          )}

          {/* ============ EMAIL INPUT ============ */}
          {mode === MODES.EMAIL_INPUT && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(isSignUp ? MODES.NAME_INPUT : MODES.SIGNIN)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '24px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back
              </button>

              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '16px',
                  background: `${C.secondary}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <Mail size={28} gradient />
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px', fontFamily: F, color: isDark ? C.white : C.black }}>
                  {isSignUp ? 'Verify your email' : 'Enter your email'}
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  We'll send you a verification code
                </p>
              </div>

              <form onSubmit={handleSendOTP}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={inputStyle}
                  autoFocus
                  data-testid="email-input"
                />

                {error && (
                  <p style={{ color: C.red, fontSize: '13px', marginTop: '12px', fontFamily: "'Manrope', sans-serif" }}>
                    {error}
                  </p>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading || !email}
                  style={{
                    ...buttonPrimary,
                    marginTop: '20px',
                    opacity: loading || !email ? 0.5 : 1,
                    cursor: loading || !email ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="send-otp-btn"
                >
                  {loading ? <Loader2Icon size={20} /> : <>Send Code <ArrowRight size={18} /></>}
                </motion.button>
              </form>
            </motion.div>
          )}

          {/* ============ OTP VERIFY ============ */}
          {mode === MODES.OTP_VERIFY && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(MODES.EMAIL_INPUT)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '24px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back
              </button>

              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px', fontFamily: F, color: isDark ? C.white : C.black }}>
                  Check your email
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  Enter the 6-digit code sent to
                </p>
                <p style={{ fontSize: '15px', fontWeight: 600, color: C.secondary, marginTop: '4px' }}>{email}</p>
              </div>

              {/* Mock OTP Display */}
              {mockOtp && (
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(16,185,129,0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16,185,129,0.3)',
                  marginBottom: '20px',
                  textAlign: 'center',
                }}>
                  <p style={{ fontSize: '12px', color: C.green, fontFamily: "'Manrope', sans-serif" }}>
                    Demo Mode - Your OTP: <strong style={{ fontSize: '16px', letterSpacing: '2px' }}>{mockOtp}</strong>
                  </p>
                </div>
              )}

              <form onSubmit={handleVerifyOTP}>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  style={{ ...inputStyle, textAlign: 'center', fontSize: '24px', letterSpacing: '8px', fontWeight: 600 }}
                  autoFocus
                  data-testid="otp-input"
                />

                {error && (
                  <p style={{ color: C.red, fontSize: '13px', marginTop: '12px', fontFamily: "'Manrope', sans-serif" }}>
                    {error}
                  </p>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  style={{
                    ...buttonPrimary,
                    marginTop: '20px',
                    opacity: loading || otp.length !== 6 ? 0.5 : 1,
                    cursor: loading || otp.length !== 6 ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="verify-otp-btn"
                >
                  {loading ? <Loader2Icon size={20} /> : <>Verify Code <ArrowRight size={18} /></>}
                </motion.button>
              </form>

              <button
                onClick={handleSendOTP}
                disabled={loading}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: C.secondary,
                  fontSize: '14px',
                  marginTop: '16px',
                  width: '100%',
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                Didn't receive code? Send again
              </button>
            </motion.div>
          )}

          {/* ============ ONBOARDING: PERSONA ============ */}
          {mode === MODES.ONBOARD_PERSONA && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <p style={{ fontSize: '13px', color: C.secondary, fontWeight: 600, marginBottom: '8px' }}>
                  STEP 1 OF 3
                </p>
                <h2 style={{ fontSize: '26px', fontWeight: 700, marginBottom: '8px', fontFamily: F, color: isDark ? C.white : C.black }}>
                  What describes you best?
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  We'll personalize your experience
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {personasConfig.map((p) => (
                  <motion.button
                    key={p.id}
                    whileHover={{ scale: 1.03, borderColor: p.color }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handlePersonaSelect(p.id)}
                    style={{
                      padding: '20px 16px',
                      borderRadius: '16px',
                      border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                    data-testid={`persona-${p.id}`}
                  >
                    <div style={{ color: p.color, marginBottom: '8px' }}><p.Icon size={28} /></div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: isDark ? C.white : C.black, marginBottom: '4px' }}>{p.title}</div>
                    <div style={{ fontSize: '12px', opacity: 0.6 }}>{p.subtitle}</div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ============ ONBOARDING: GOAL ============ */}
          {mode === MODES.ONBOARD_GOAL && selectedPersona && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(MODES.ONBOARD_PERSONA)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '24px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back
              </button>

              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <p style={{ fontSize: '13px', color: C.secondary, fontWeight: 600, marginBottom: '8px' }}>
                  STEP 2 OF 3
                </p>
                <h2 style={{ fontSize: '26px', fontWeight: 700, marginBottom: '8px', fontFamily: F, color: isDark ? C.white : C.black }}>
                  What's your main goal?
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                  We'll prioritize what matters most
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {goalsByPersona[selectedPersona]?.map((g) => (
                  <motion.button
                    key={g.id}
                    whileHover={{ scale: 1.02, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleGoalSelect(g.id)}
                    style={{
                      padding: '16px 20px',
                      borderRadius: '12px',
                      border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                    data-testid={`goal-${g.id}`}
                  >
                    <span style={{ fontWeight: 600, fontSize: '15px', color: isDark ? C.white : C.black }}>{g.label}</span>
                    <ChevronRight size={18} style={{ opacity: 0.5 }} />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ============ ONBOARDING: COMPANY ============ */}
          {mode === MODES.ONBOARD_PLAN && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(MODES.ONBOARD_GOAL)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '24px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back
              </button>

              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <p style={{ fontSize: '13px', color: C.secondary, fontWeight: 600, marginBottom: '8px' }}>
                  STEP 3 OF 3
                </p>
                <h2 style={{ fontSize: '26px', fontWeight: 700, marginBottom: '8px', fontFamily: F, color: isDark ? C.white : C.black }}>
                  Choose Your Plan
                </h2>
                <p style={{ fontSize: '14px', opacity: 0.7, fontFamily: "'Manrope', sans-serif", marginBottom: '20px' }}>
                  Start free, upgrade anytime
                </p>
                
                {/* Billing Cycle & Currency Toggles */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  {/* Billing Cycle Toggle */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    borderRadius: '100px',
                    padding: '4px',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
                  }}>
                    {['monthly', 'yearly'].map((cycle) => (
                      <button
                        key={cycle}
                        onClick={() => setBillingCycle(cycle)}
                        style={{
                          padding: '8px 18px',
                          borderRadius: '100px',
                          border: 'none',
                          background: billingCycle === cycle ? C.grad : 'transparent',
                          color: billingCycle === cycle ? '#fff' : (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontFamily: F,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        data-testid={`billing-${cycle}`}
                      >
                        {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                        {cycle === 'yearly' && (
                          <span style={{
                            background: '#10B981',
                            color: '#fff',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '10px',
                            fontWeight: 700
                          }}>
                            SAVE 17%
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Currency Toggle */}
                  <div style={{
                    display: 'flex',
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    borderRadius: '100px',
                    padding: '4px',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
                  }}>
                    {['INR', 'USD'].map((curr) => (
                      <button
                        key={curr}
                        onClick={() => setCurrency(curr)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '100px',
                          border: 'none',
                          background: currency === curr ? (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)') : 'transparent',
                          color: currency === curr ? (isDark ? '#fff' : '#1a1a2e') : (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'),
                          fontWeight: 600,
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontFamily: F
                        }}
                        data-testid={`currency-${curr.toLowerCase()}`}
                      >
                        {curr === 'INR' ? '₹ INR' : '$ USD'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px',
                marginBottom: '24px'
              }}>
                {onboardingPlansConfig.map((plan, i) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08 }}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => handlePlanSelect(plan)}
                    style={{
                      padding: '16px',
                      borderRadius: '14px',
                      background: selectedPlan?.id === plan.id 
                        ? `${plan.color}15` 
                        : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      border: selectedPlan?.id === plan.id 
                        ? `2px solid ${plan.color}` 
                        : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s ease'
                    }}
                    data-testid={`plan-${plan.id}`}
                  >
                    {plan.badge && (
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '8px',
                        background: C.grad,
                        color: '#fff',
                        padding: '3px 8px',
                        borderRadius: '8px',
                        fontSize: '9px',
                        fontWeight: 700
                      }}>
                        {plan.badge}
                      </div>
                    )}
                    
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ color: plan.color }}><plan.Icon size={22} /></span>
                      <span style={{ 
                        fontSize: '15px', 
                        fontWeight: 700,
                        color: isDark ? '#fff' : '#1a1a2e'
                      }}>
                        {plan.name}
                      </span>
                    </div>
                    
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 700, 
                      marginBottom: '10px',
                      color: isDark ? '#fff' : '#1a1a2e'
                    }}>
                      {getPrice(plan) === 0 ? 'Free' : (
                        <>
                          {formatPrice(getPrice(plan))}
                          <span style={{ fontSize: '12px', fontWeight: 500, opacity: 0.7 }}>
                            /{billingCycle === 'yearly' ? 'yr' : 'mo'}
                          </span>
                          {billingCycle === 'yearly' && getMonthlyEquivalent(plan) > 0 && (
                            <div style={{ 
                              fontSize: '11px', 
                              fontWeight: 500, 
                              color: C.secondary,
                              marginTop: '2px'
                            }}>
                              {formatPrice(getMonthlyEquivalent(plan))}/mo
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div style={{ fontSize: '11px', color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                      {plan.features.map((f, j) => (
                        <div key={j} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          marginBottom: '5px'
                        }}>
                          <Check size={12} gradient />
                          {f}
                        </div>
                      ))}
                    </div>
                    
                    {selectedPlan?.id === plan.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: plan.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Check size={12} color="#fff" />
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(0,130,251,0.4)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePlanSubmit}
                disabled={!selectedPlan}
                style={{
                  width: '100%',
                  background: selectedPlan ? C.grad : 'rgba(128,128,128,0.3)',
                  color: C.white,
                  padding: '14px',
                  borderRadius: '100px',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '15px',
                  cursor: selectedPlan ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontFamily: F
                }}
                data-testid="plan-continue-btn"
              >
                {selectedPlan?.id === 'free' ? 'Start Free' : `Continue with ${selectedPlan?.name || 'Plan'}`}
                <ArrowRight size={18} />
              </motion.button>
              
              <p style={{ 
                textAlign: 'center', 
                fontSize: '12px', 
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', 
                marginTop: '12px',
                fontFamily: "'Manrope', sans-serif"
              }}>
                {selectedPlan?.id === 'free' 
                  ? 'No credit card required' 
                  : 'Proceed to secure payment'}
              </p>
            </motion.div>
          )}

          {/* ============ PAYMENT STEP ============ */}
          {mode === MODES.PAYMENT && selectedPlan && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setMode(MODES.ONBOARD_PLAN)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                  marginBottom: '20px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '14px',
                }}
              >
                <ArrowLeft size={18} />
                Back to plans
              </button>

              {/* Header with Plan Icon */}
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${selectedPlan.color}30, ${selectedPlan.color}10)`,
                  border: `2px solid ${selectedPlan.color}40`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: `0 8px 32px ${selectedPlan.color}20`
                }}>
                  <CreditCardIcon size={32} />
                </div>
                <h2 style={{ 
                  fontSize: '26px', 
                  fontWeight: 700, 
                  marginBottom: '6px', 
                  fontFamily: F, 
                  color: isDark ? C.white : C.black 
                }}>
                  Complete Payment
                </h2>
                <p style={{ 
                  fontSize: '14px', 
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                  fontFamily: "'Manrope', sans-serif" 
                }}>
                  Secure payment powered by Razorpay
                </p>
              </div>

              {/* Order Summary Card */}
              <div style={{
                background: isDark 
                  ? 'linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))'
                  : 'linear-gradient(145deg, rgba(0,0,0,0.03), rgba(0,0,0,0.01))',
                borderRadius: '20px',
                padding: '24px',
                marginBottom: '20px',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: `${selectedPlan.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ color: selectedPlan.color }}><selectedPlan.Icon size={32} /></span>
                    </div>
                    <div>
                      <div style={{ 
                        fontSize: '17px',
                        fontWeight: 700,
                        color: isDark ? '#fff' : '#1a1a2e',
                        marginBottom: '2px'
                      }}>
                        {selectedPlan.name} Plan
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: C.secondary,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {billingCycle === 'yearly' ? 'Annual Billing' : 'Monthly Billing'}
                      </div>
                    </div>
                  </div>
                  {billingCycle === 'yearly' && (
                    <div style={{
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      color: '#fff',
                      padding: '4px 10px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: 700
                    }}>
                      SAVE 17%
                    </div>
                  )}
                </div>
                
                {/* Features */}
                <div style={{ marginBottom: '20px' }}>
                  {selectedPlan.features.map((f, j) => (
                    <div key={j} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      marginBottom: '10px',
                      fontSize: '14px',
                      color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Check size={12} gradient />
                      </div>
                      {f}
                    </div>
                  ))}
                </div>
                
                {/* Price Breakdown */}
                <div style={{
                  background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <span style={{ 
                      fontSize: '14px',
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                    }}>
                      Subtotal ({billingCycle})
                    </span>
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: 500,
                      color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
                    }}>
                      {formatPrice(getPrice(selectedPlan))}
                    </span>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: '12px',
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
                  }}>
                    <span style={{ 
                      fontSize: '16px', 
                      fontWeight: 700,
                      color: isDark ? '#fff' : '#1a1a2e'
                    }}>
                      Total
                    </span>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ 
                        fontSize: '26px', 
                        fontWeight: 700,
                        background: C.grad,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {formatPrice(getPrice(selectedPlan))}
                      </span>
                      {billingCycle === 'yearly' && getMonthlyEquivalent(selectedPlan) > 0 && (
                        <div style={{ 
                          fontSize: '12px', 
                          color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                          marginTop: '2px'
                        }}>
                          ({formatPrice(getMonthlyEquivalent(selectedPlan))}/month)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {paymentError && (
                <div style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  color: '#EF4444',
                  fontSize: '14px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <AlertCircleIcon size={16} />
                  {paymentError}
                </div>
              )}

              {/* Pay Button */}
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 12px 40px rgba(0,130,251,0.4)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePayment}
                disabled={paymentLoading}
                style={{
                  width: '100%',
                  padding: '18px',
                  borderRadius: '14px',
                  border: 'none',
                  background: paymentLoading 
                    ? 'rgba(128,128,128,0.3)' 
                    : 'linear-gradient(135deg, #0082FB 0%, #00D4FF 100%)',
                  color: '#fff',
                  fontSize: '17px',
                  fontWeight: 700,
                  cursor: paymentLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontFamily: F,
                  boxShadow: paymentLoading ? 'none' : '0 8px 30px rgba(0,130,251,0.3)',
                  transition: 'all 0.2s ease'
                }}
                data-testid="pay-now-btn"
              >
                {paymentLoading ? (
                  <>
                    <Loader2Icon size={22} />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <ShieldIcon size={20} />
                    Pay {formatPrice(getPrice(selectedPlan))} Securely
                  </>
                )}
              </motion.button>

              {/* Security Badges */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '20px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                  fontSize: '12px'
                }}>
                  <ShieldIcon size={14} />
                  <span>256-bit SSL</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                  fontSize: '12px'
                }}>
                  <CreditCardIcon size={14} />
                  <span>Powered by Razorpay</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* ============ CREATING ============ */}
          {mode === MODES.CREATING && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ textAlign: 'center', padding: '20px 0' }}
            >
              {/* Show payment loader if a paid plan was selected */}
              {selectedPlan && getPrice(selectedPlan) > 0 ? (
                <PaymentLoader 
                  amount={getPrice(selectedPlan)}
                  currency={currency}
                  minDuration={20000}
                />
              ) : (
                <>
                  <BrandedLoader text="Setting up your account..." showFacts={true} />
                </>
              )}
            </motion.div>
          )}

          {/* ============ SUCCESS ============ */}
          {mode === MODES.SUCCESS && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ textAlign: 'center' }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: `${C.green}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <Check size={40} gradient />
              </motion.div>

              <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px', fontFamily: F, color: isDark ? C.white : C.black }}>
                Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
              </h2>
              <p style={{ fontSize: '15px', opacity: 0.7, fontFamily: "'Manrope', sans-serif" }}>
                Your account is ready. Redirecting to dashboard...
              </p>

              {user && (
                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  {user.profile_picture && (
                    <img src={user.profile_picture} alt={user.name} style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                  )}
                  <div style={{ textAlign: 'left' }}>
                    <p style={{ fontWeight: 600, fontSize: '15px', color: isDark ? C.white : C.black }}>{user.name}</p>
                    <p style={{ fontSize: '13px', opacity: 0.6 }}>{user.email}</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;
